import { AxiosError } from 'axios';
import { AppError, EAppErrorKind, isAppError } from '@/utils/error';
import { isIResultData } from './result';

const STATUS_KIND_MAP: Readonly<Record<number, EAppErrorKind>> = {
  400: EAppErrorKind.BAD_REQUEST,
  401: EAppErrorKind.UNAUTHORIZED,
  403: EAppErrorKind.FORBIDDEN,
  404: EAppErrorKind.NOT_FOUND,
  409: EAppErrorKind.CONFLICT,
  422: EAppErrorKind.UNPROCESSABLE,
  423: EAppErrorKind.LOCKED,
  429: EAppErrorKind.RATE_LIMITED
};

/**
 * HTTP 状态码与业务码共用一张映射表。
 * 5xx 兜底为 SERVER 时必须限定在 500-599:业务码是一套自定义编号,
 * 4001 这类取值不能因为「大于 500」就被当成服务端错误。
 */
function kindFromStatus(status: number | undefined): EAppErrorKind {
  if (status === undefined) return EAppErrorKind.UNKNOWN;
  const mapped = STATUS_KIND_MAP[status];
  if (mapped) return mapped;
  return status >= 500 && status <= 599 ? EAppErrorKind.SERVER : EAppErrorKind.UNKNOWN;
}

/** 拿不到响应的错误码:断网与超时 */
const NETWORK_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  AxiosError.ERR_NETWORK,
  AxiosError.ECONNABORTED,
  AxiosError.ETIMEDOUT
]);

/**
 * isIResultData 只做结构判断,不校验字段类型,
 * 因此取值时单独确认类型,避免把非法值当成 number / string 传下去。
 */
function readResult(data: unknown): { bizCode?: number; serverMessage?: string } {
  if (!isIResultData(data)) return {};
  return {
    bizCode: typeof data.code === 'number' ? data.code : undefined,
    serverMessage: typeof data.message === 'string' ? data.message : undefined
  };
}

function fromAxiosError(error: AxiosError): AppError {
  if (error.code === AxiosError.ERR_CANCELED) {
    return new AppError(EAppErrorKind.CANCELED, { cause: error });
  }

  const { response } = error;
  // 无响应即视为网络问题,超时(ECONNABORTED/ETIMEDOUT)一并归入,不再落到通用兜底
  if (
    !response ||
    (error.code !== undefined && NETWORK_ERROR_CODES.has(error.code)) ||
    error.message === 'Network Error'
  ) {
    return new AppError(EAppErrorKind.NETWORK, { cause: error });
  }

  return new AppError(kindFromStatus(response.status), {
    httpStatus: response.status,
    cause: error,
    ...readResult(response.data)
  });
}

/**
 * 把任意错误归一化成 AppError。
 *
 * 幂等 —— 传入 AppError 时原样返回,因此本地 `throw new AppError(...)`
 * 抛出的错误无需在这里单独开分支。
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error;

  if (error instanceof AxiosError) return fromAxiosError(error);

  // 响应拦截器对业务码非 200 的响应直接 reject 裸 IResultData
  if (isIResultData(error)) {
    const result = readResult(error);
    return new AppError(kindFromStatus(result.bizCode), { ...result, cause: error });
  }

  return new AppError(EAppErrorKind.UNKNOWN, { cause: error });
}
